import { useCallback, useEffect, useRef } from "react";
import { DTRM_ID_ATTR } from "./collapseIntoDrag";

type Point = { x: number; y: number };

/** Deal card uses `mx-6` (24px) — include that gutter as a marquee start zone. */
export const MARQUEE_SIDE_GUTTER_PX = 24;

/** Ensure only one active marquee drag exists (guards against HMR / strict-mode stacks). */
let disposeActiveMarqueeListeners: (() => void) | null = null;

function rectsIntersect(
  a: { left: number; top: number; right: number; bottom: number },
  b: { left: number; top: number; right: number; bottom: number },
) {
  return !(
    a.right < b.left ||
    a.left > b.right ||
    a.bottom < b.top ||
    a.top > b.bottom
  );
}

function isInteractiveTarget(target: HTMLElement) {
  return Boolean(
    target.closest(`[${DTRM_ID_ATTR}]`) ||
      target.closest("button, a, input, label, [role='menu'], [role='listbox']"),
  );
}

/**
 * Marquee selection. Initiation is via returned handlers (surface + gutter strips).
 * mousemove/mouseup are bound only while a drag is active, then removed.
 */
export function useMarqueeSelection(args: {
  enabled: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
  onSelectIds: (ids: string[]) => void;
}) {
  const { enabled, containerRef, onSelectIds } = args;
  const overlayRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<Point | null>(null);
  const activeRef = useRef(false);
  const onSelectIdsRef = useRef(onSelectIds);
  onSelectIdsRef.current = onSelectIds;

  const hideOverlay = useCallback(() => {
    const el = overlayRef.current;
    if (!el) return;
    el.style.display = "none";
  }, []);

  const showOverlay = useCallback(
    (left: number, top: number, width: number, height: number) => {
      const el = overlayRef.current;
      if (!el) return;
      el.style.display = "block";
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.width = `${width}px`;
      el.style.height = `${height}px`;
    },
    [],
  );

  const endMarquee = useCallback(() => {
    startRef.current = null;
    activeRef.current = false;
    hideOverlay();
    disposeActiveMarqueeListeners?.();
    disposeActiveMarqueeListeners = null;
    document.body.style.removeProperty("user-select");
    document.body.style.removeProperty("cursor");
  }, [hideOverlay]);

  const beginMarquee = useCallback(
    (clientX: number, clientY: number) => {
      if (!enabled || !containerRef.current) return;

      disposeActiveMarqueeListeners?.();

      startRef.current = { x: clientX, y: clientY };
      activeRef.current = true;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "crosshair";
      showOverlay(clientX, clientY, 0, 0);

      const onMove = (e: MouseEvent) => {
        if (!activeRef.current || !startRef.current) return;
        const left = Math.min(startRef.current.x, e.clientX);
        const top = Math.min(startRef.current.y, e.clientY);
        const width = Math.abs(e.clientX - startRef.current.x);
        const height = Math.abs(e.clientY - startRef.current.y);
        showOverlay(left, top, width, height);
      };

      const onUp = (e: MouseEvent) => {
        if (!activeRef.current || !startRef.current || !containerRef.current) {
          endMarquee();
          return;
        }

        const left = Math.min(startRef.current.x, e.clientX);
        const top = Math.min(startRef.current.y, e.clientY);
        const right = Math.max(startRef.current.x, e.clientX);
        const bottom = Math.max(startRef.current.y, e.clientY);
        const selectionBox = { left, top, right, bottom };

        const dragged =
          Math.abs(right - left) > 4 || Math.abs(bottom - top) > 4;

        if (!dragged) {
          onSelectIdsRef.current([]);
          endMarquee();
          return;
        }

        const nodes = containerRef.current.querySelectorAll<HTMLElement>(
          `[${DTRM_ID_ATTR}]`,
        );
        const ids: string[] = [];
        nodes.forEach((node) => {
          const id = node.getAttribute(DTRM_ID_ATTR);
          if (!id) return;
          const r = node.getBoundingClientRect();
          if (
            rectsIntersect(selectionBox, {
              left: r.left,
              top: r.top,
              right: r.right,
              bottom: r.bottom,
            })
          ) {
            ids.push(id);
          }
        });

        onSelectIdsRef.current(ids);
        endMarquee();
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      disposeActiveMarqueeListeners = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
    },
    [enabled, containerRef, showOverlay, endMarquee],
  );

  useEffect(() => {
    if (!enabled) {
      endMarquee();
    }
    return () => {
      endMarquee();
    };
  }, [enabled, endMarquee]);

  const onSurfaceMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (!target || isInteractiveTarget(target)) return;

      e.preventDefault();
      beginMarquee(e.clientX, e.clientY);
    },
    [enabled, beginMarquee],
  );

  const onGutterMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      beginMarquee(e.clientX, e.clientY);
    },
    [enabled, beginMarquee],
  );

  return {
    overlayRef,
    onSurfaceMouseDown,
    onGutterMouseDown,
  };
}
